import { useState, useEffect, useCallback, useRef } from "react";
import { apiClient } from "@/lib/api";
import { ArtieScoreData, Recommendation } from "@/types/artie-score";
import { useToast } from "@/hooks/use-toast";
import { useQueryClient } from "@tanstack/react-query";
import { useInventoryMode } from "@/hooks/useInventoryMode";

interface UseArtieScoreResult {
  scoreData: ArtieScoreData | null;
  loading: boolean;
  error: string | null;
  currentStep: number;
  statusMessage: string;
  progressPercent: number;
  refetch: () => Promise<void>;
  refresh: () => Promise<void>;
  acceptRecommendation: (rec: Recommendation) => Promise<void>;
  dismissRecommendation: (rec: Recommendation) => Promise<void>;
}

export const useArtieScore = (projectId: string | undefined, suppressToast = false): UseArtieScoreResult => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  // THE ONE PLACE THE ARTIE SCORE IS STOPPED IN THE FRONTEND.
  //
  // In inventory mode an uploaded script goes to the placement agent instead, and the backend guards
  // the score task bodies — so the score is not merely absent, it is never coming. That makes every
  // screen that waits for one unresolvable, not slow.
  //
  // This is enforced HERE rather than on each screen because per-screen guards demonstrably do not
  // hold: four were added by hand and a fifth upload flow was still unguarded, while fourteen files
  // in total reach for a score. Twelve of them come through this hook, so stopping it here covers
  // them all at once — including screens nobody has written yet.
  const inventoryMode = useInventoryMode();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [scoreData, setScoreData] = useState<ArtieScoreData | null>(() => {
    if (projectId) {
      const cached = queryClient.getQueryData<ArtieScoreData>(["artieScore", projectId]);
      if (cached) return cached;
    }
    return null;
  });
  const [currentStep, setCurrentStep] = useState(0);
  const [statusMessage, setStatusMessage] = useState("Initializing...");
  const [progressPercent, setProgressPercent] = useState(0);
  const [progressHistory, setProgressHistory] = useState<Array<{ percent: number, timestamp: number }>>([]);
  const [isManualRefetch, setIsManualRefetch] = useState(false);
  const eventSourceRef = useRef<EventSource | null>(null);

  // Helper to connect to SSE stream
  const connectSSE = useCallback((pid: string) => {
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
    }

    const baseUrl = import.meta.env.VITE_API_BASE_URL || '';
    const sseUrl = `${baseUrl}/api/v1/sse/project/${pid}`;

    console.log("Connecting to SSE:", sseUrl);
    const eventSource = new EventSource(sseUrl);
    eventSourceRef.current = eventSource;

    eventSource.onopen = () => {
      console.log("SSE Connection opened");
    };

    eventSource.addEventListener("update", (event: MessageEvent) => {
      try {
        const data = JSON.parse(event.data);
        console.log("SSE Update received:", data);

        if (data.percent !== undefined) {
          setProgressPercent(data.percent);

          // Update step based on progress percentage
          if (data.percent < 30) {
            setCurrentStep(0); // Processing script upload
          } else if (data.percent < 80) {
            setCurrentStep(1); // Analyzing script
          } else {
            setCurrentStep(2); // Calculating score
          }
        }

        if (data.status) {
          setStatusMessage(data.status);
        }

        // Check for completion
        if (data.percent === 100 || (data.status && data.status.toLowerCase().includes("complete"))) {
          if (data.score_data) {
            console.log("SSE: Score data received via stream!");
            setScoreData(data.score_data as ArtieScoreData);
            setLoading(false);
            eventSource.close();
            eventSourceRef.current = null;
          } else {
            // Fallback: fetch score if not in payload
            apiClient.getArtieScoreByProject(pid)
              .then((score) => {
                setScoreData(score as ArtieScoreData);
                setLoading(false);
                eventSource.close();
                eventSourceRef.current = null;
              })
              .catch(err => console.error("Error fetching score after SSE complete:", err));
          }
        }
      } catch (e) {
        console.error("Error parsing SSE data:", e);
      }
    });

    eventSource.onerror = (err) => {
      console.error("SSE Error:", err);
      // Depending on error type, might want to close or retry
      // For now, let browser retry or close if persistent
      if (eventSource.readyState === EventSource.CLOSED) {
        eventSourceRef.current = null;
      }
    };

    return eventSource;
  }, []);

  const fetchArtieScore = useCallback(async (forceRecalculate = false) => {
    if (!projectId) {
      setError("No project ID provided");
      setLoading(false);
      return;
    }

    // Guarded here rather than only in the mount effect so that refetch() and refresh() — which
    // several screens wire to a "Recalculate" button — cannot start the work either.
    if (inventoryMode) {
      setError("Scoring is turned off while we build your placement inventory.");
      setLoading(false);
      return;
    }

    // If we have cached score and not forcing recalculation, we can avoid loading state
    const cachedScore = queryClient.getQueryData<ArtieScoreData>(["artieScore", projectId]);
    if (cachedScore && !forceRecalculate) {
      setScoreData(cachedScore);
      setLoading(false);
      setProgressPercent(100);
      setStatusMessage("Complete!");
      setCurrentStep(2);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      if (forceRecalculate) {
        // Reset progress immediately when starting recalculation
        setProgressPercent(0);
        setCurrentStep(0);
        setStatusMessage("Initializing recalculation...");
        setProgressHistory([]);

        // Force recalculation using the recalculate endpoint
        const response = await apiClient.recalculateArtieScore(projectId) as any;
        console.log("Recalculate response:", response);

        // Connect to SSE for updates
        if (response.status === "processing") {
          connectSSE(projectId);
        } else if (response.status === "completed" && response.score_data) {
          // Immediate sync completion
          setScoreData(response.score_data as ArtieScoreData);
          setLoading(false);
        }

      } else {
        // Check existing status first
        const statusResponse = await apiClient.getArtieScoreStatus(projectId) as any;
        console.log("Initial status check:", statusResponse);

        if (statusResponse.status === "completed" && statusResponse.score_data) {
          setScoreData(statusResponse.score_data as ArtieScoreData);
          setLoading(false);
          setProgressPercent(100);
          setStatusMessage("Complete!");
          setCurrentStep(2);
        } else if (statusResponse.status === "processing" || (statusResponse.status === "completed" && !statusResponse.score_data)) {
          // It's running, connect to SSE
          setProgressPercent(statusResponse.progress?.percent || 0);
          setStatusMessage(statusResponse.progress?.status || "Processing...");
          connectSSE(projectId);
        } else if (statusResponse.status === "failed") {
          setError(statusResponse.error || "Calculation failed previously");
          setLoading(false);
        } else {
          // Not started, trigger calculation
          console.log("Not started, triggering calculation...");
          try {
            await apiClient.calculateArtieScore(projectId, false);
            connectSSE(projectId);
          } catch (err: any) {
            console.error("Failed to trigger calculation:", err);
            setError("Failed to start calculation");
            setLoading(false);
          }
        }
      }
    } catch (err: any) {
      console.error("Error fetching Artie Score:", err);
      setError(err.message || "Failed to load Artie Score");
      setLoading(false);
    }
  }, [projectId, connectSSE, inventoryMode]);

  // Cleanup SSE on unmount
  useEffect(() => {
    return () => {
      if (eventSourceRef.current) {
        console.log("Cleaning up SSE connection");
        eventSourceRef.current.close();
      }
    };
  }, []);

  const acceptRecommendation = async (rec: Recommendation) => {
    if (!projectId) return;
    try {
      const updatedScore = await apiClient.acceptArtieRecommendation(projectId, rec.what_to_improve);
      setScoreData(updatedScore as ArtieScoreData);
      toast({
        title: "Recommendation Accepted",
        description: "Added to your task list.",
      });
    } catch (err: any) {
      console.error("Error accepting recommendation:", err);
      toast({
        title: "Error",
        description: "Failed to accept recommendation",
        variant: "destructive",
      });
    }
  };

  const dismissRecommendation = async (rec: Recommendation) => {
    if (!projectId) return;
    try {
      const updatedScore = await apiClient.dismissArtieRecommendation(projectId, rec.what_to_improve);
      setScoreData(updatedScore as ArtieScoreData);
      toast({
        title: "Recommendation Dismissed",
        description: "Removed from your list.",
      });
    } catch (err: any) {
      console.error("Error dismissing recommendation:", err);
      toast({
        title: "Error",
        description: "Failed to dismiss recommendation",
        variant: "destructive",
      });
    }
  };

  useEffect(() => {
    // WAIT for the mode answer instead of guessing. `null` means the server has not answered yet, and
    // it is NOT the same as false: firing the fetch on null would POST calculateArtieScore before the
    // guard could stop it, which is the race the previous per-screen guards lost — their redirect
    // effect ran after this hook's mount effect had already started the work.
    if (inventoryMode === null) return;
    if (inventoryMode) {
      setError("Scoring is turned off while we build your placement inventory.");
      setLoading(false);
      return;
    }
    // Only run initial fetch if not doing a manual refetch
    if (!isManualRefetch && projectId) {
      fetchArtieScore();
    }
  }, [projectId, fetchArtieScore, inventoryMode]);

  const refetch = useCallback(async () => {
    setIsManualRefetch(true);
    await fetchArtieScore(true);
    setIsManualRefetch(false);
  }, [projectId]);

  const refresh = useCallback(async () => {
    setIsManualRefetch(true);
    await fetchArtieScore(false);
    setIsManualRefetch(false);
  }, [projectId]);

  return {
    scoreData,
    loading,
    error,
    currentStep,
    statusMessage,
    progressPercent,
    refetch, // Force recalculation
    refresh, // Just fetch existing score
    acceptRecommendation,
    dismissRecommendation
  };
};